/* eslint-disable react/jsx-props-no-spreading */
import React from "react";
import { TreeView } from "@material-ui/lab";
import { makeStyles, Menu, MenuItem, Divider, Theme } from "@material-ui/core";
import { AlertDialogData } from "../../types";
import { MinusSquareSvgIcon } from "../../components/tree/icon/MinusSquareSvgIcon";
import { PlusSquareSvgIcon } from "../../components/tree/icon/PlusSquareSvgIcon";
import { CloseSquareSvgIcon } from "../../components/tree/icon/CloseSquareSvgIcon";
import {
  VirtualFolderTreeItem,
  EditNodeInfoType,
} from "./VirtualFolderTreeItem";
import {
  TreeDataType,
  StructureType,
  ToNest,
  MoveNode,
  RemoveNode,
  ExistsNode,
  RemoveExpanded,
  MoveExpanded,
  CountOtherTreeNode,
  OtherTreeNodeLabel,
  OtherTreeNodeName,
  PathToGrandParentNodeOnList,
  PathToBrothersNode,
  ExistsMoveTarget,
  ActionNameRename,
  IsCategoryNode,
} from "./model/TreeItemModel";

/** スタイル定義 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useStyles = makeStyles((_theme: Theme) => ({
  root: {
    height: "100%",
    flexGrow: 1,
    maxWidth: 400,
  },
}));

interface Props {
  treeData: TreeDataType;
  editNodeInfo: EditNodeInfoType;
  getTreeDataById: (id: number | undefined) => TreeDataType | undefined;
  setTreeDataById: (
    id: number | undefined,
    structure: StructureType[] | undefined,
    expandedNode: string[] | undefined,
    isDisplayAll: boolean | undefined,
    update: boolean
  ) => void;
  setEditNodeInfo: any;
  setRenderNodeStates: (updateRenderNodeId: string) => void;
  handleClickNode: (
    event: React.MouseEvent<HTMLElement>,
    nodeId: string,
    configId: number,
    excludes?: string[]
  ) => void;
  addTreeNode: (nodeId: string) => void;
  setAlertDialogData: React.Dispatch<React.SetStateAction<AlertDialogData>>;
  isAdmin: boolean;
}

/** 仮想フォルダ―ツリー */
export const VirtualFolderTree = (props: Props) => {
  const classes = useStyles();
  const {
    treeData,
    editNodeInfo,
    setEditNodeInfo,
    setRenderNodeStates,
    handleClickNode,
    getTreeDataById,
    setTreeDataById,
    addTreeNode,
    setAlertDialogData,
    isAdmin,
  } = props;

  const configId = treeData.virtual_folder_config_id;
  const { structure, expandedNode, isDisplayAll } = treeData;
  // 仮想フォルダ―のツリーのフィルターを掛けている場合の、
  // その他フォルダの表示制御に使用
  const isFiltered =
    structure.findIndex(el => el.isDisplaySelfOnFilter === false) > 0;

  //------------------------------
  // 処理選択コンテキスト関連

  // メニューラベル
  const textAddTreeNodeMenu = "新しいフォルダ―";
  const textRenameTreeNodeMenu = "名前の変更";
  const TextRemoveTreeNodeMenu = (nodeId: string) => {
    return IsCategoryNode(nodeId) ? "カテゴリーの削除" : "フォルダ―の削除";
  };
  const textMoveTreeNodeMenu = "フォルダ―の移動";

  const contextMenuInitData = {
    mouseX: null,
    mouseY: null,
  };

  const onOpenContextMenuState = (event: React.MouseEvent<HTMLElement>) => {
    return {
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    };
  };

  //------------------------------
  // 移動先フォルダ―選択コンテキスト関連

  const [treeContextMenuState, setTreeContextMenuState] = React.useState<{
    mouseX: null | number;
    mouseY: null | number;
  }>(contextMenuInitData);

  const [treeContextMenuOnMove, setTreeContextMenuOnMove] = React.useState<{
    mouseX: null | number;
    mouseY: null | number;
  }>(contextMenuInitData);

  //------------------------------
  // [Handler]

  /** コンテキストメニュー表示(追加／名前変更／削除／移動) */
  const handleTreeContextMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    nodeId: string
  ) => {
    event.preventDefault();
    event.stopPropagation();

    // 編集ノードのステートセット
    setEditNodeInfo({
      selectedNodeId: nodeId,
      editNodeId: "",
      treeEditMode: "",
      renderNodeId: "",
    });

    // コンテキストメニュー表示
    setTreeContextMenuState(onOpenContextMenuState(event));
  };

  /** コンテキストメニュー表示(その他フォルダ―用のダミー処理) */
  const handleOtherTreeContextMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _nodeId: string
  ) => {
    // コンテキストメニューさせない
    event.preventDefault();
    event.stopPropagation();
    // コンテキストメニュー表示
    setTreeContextMenuState(contextMenuInitData);
  };

  /** コンテキストメニュー表示（フォルダ―移動） */
  const handleTreeContextMenuOnMoveClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    // コンテキストメニュー表示
    setTreeContextMenuOnMove(onOpenContextMenuState(event));
  };

  /** コンテキストメニュークローズ処理 */
  const handleTreeContextMenuClose = () => {
    setTreeContextMenuState(contextMenuInitData);
    setTreeContextMenuOnMove(contextMenuInitData);
  };

  /** ノードの開閉時の処理 */
  const handleNodeToggle = (
    _event: React.ChangeEvent<{}>,
    nodeIds: string[]
  ) => {
    setTreeDataById(configId, undefined, nodeIds, undefined, false);
  };

  /** 新規フォルダ―作成(仮フォルダ―描画) */
  const handleAddTreeNode = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    addTreeNode(editNodeInfo.selectedNodeId);

    // コンテキストメニュー初期化
    setTreeContextMenuState(contextMenuInitData);
  };

  /** フォルダ―名変更(仮フォルダ―描画) */
  const handleRenameTreeNode = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    // 編集ノードのステートセット
    setEditNodeInfo({
      selectedNodeId: editNodeInfo.selectedNodeId,
      editNodeId: editNodeInfo.selectedNodeId,
      treeEditMode: ActionNameRename,
      renderNodeId: editNodeInfo.selectedNodeId,
    });

    // コンテキストメニュー初期化
    setTreeContextMenuState(contextMenuInitData);
  };

  /** フォルダ―移動(本処理) */
  const handleMoveTreeNodeRegist = (
    selectNodeId: string,
    targetNodeId: string
  ) => {
    const tree = getTreeDataById(configId);
    if (!tree) return;
    // 移動元と移動先が同じ場合、移動元や移動先が存在しない場合は移動しない
    if (
      selectNodeId === targetNodeId ||
      !ExistsNode(tree.structure, selectNodeId) ||
      !ExistsNode(tree.structure, targetNodeId)
    )
      return;
    // フォルダ―の移動（パスの書き換え）
    const movedNode = MoveNode(tree.structure, selectNodeId, targetNodeId);
    // [HP稼働] 階層制限を解除
    // const isOver = OverMaxTreeHierarchy(movedNode);
    const isOver = false;
    if (isOver) {
      // 階層が設定を超える場合のエラー通知
      setAlertDialogData({
        open: true,
        title: "エラー",
        message: "階層の許容値を超えます。移動できません。",
      });
    } else {
      // 展開ノードのパスも合わせて変更する
      const movedExpanded = MoveExpanded(
        tree.expandedNode,
        selectNodeId,
        targetNodeId
      );
      // ステートのセット、API連携、ローカルストレージセット
      setTreeDataById(configId, movedNode, movedExpanded, undefined, true);
    }
    // 編集用ノードのレンダー用の更新(移動先のフォルダ―以下を更新)
    setRenderNodeStates(targetNodeId);
    // コンテキストメニュー初期化
    setTreeContextMenuState(contextMenuInitData);
    setTreeContextMenuOnMove(contextMenuInitData);
  };

  /** フォルダ―移動(イベントハンドルのみ) */
  const handleMoveTreeNode = (
    event: React.MouseEvent<HTMLElement>,
    targetNodeId: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    // フォルダ―移動(本処理)
    handleMoveTreeNodeRegist(editNodeInfo.selectedNodeId, targetNodeId);
  };

  /** フォルダ―削除(削除確定) */
  const handleRemoveTreeNodeRegist = (selectNodeId: string) => {
    const tree = getTreeDataById(configId);
    if (!tree) return;
    const existSelectedPath = ExistsNode(tree.structure, selectNodeId);
    if (existSelectedPath) {
      // 再帰的に削除
      const removedNode = RemoveNode(tree.structure, selectNodeId);
      // 展開ノードのパスも合わせて変更する
      const removedExpanded = RemoveExpanded(tree.expandedNode, removedNode);
      // ステートのセット、API連携、ローカルストレージセット
      setTreeDataById(configId, removedNode, removedExpanded, undefined, true);
    }
    // 編集用ノードのレンダー用の更新(削除フォルダ―以下を更新)
    setRenderNodeStates(selectNodeId);
    // コンテキストメニュー初期化
    setTreeContextMenuState(contextMenuInitData);
  };

  /** フォルダ―削除(イベントハンドルのみ) */
  const handleRemoveTreeNode = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // フォルダ―削除(本処理)
    handleRemoveTreeNodeRegist(editNodeInfo.selectedNodeId);
  };

  //------------------------------
  // コンポーネントの描画

  /** コンテキストメニュー(追加／名前変更／削除／移動) */
  const contextMenu = (
    <Menu
      keepMounted={false}
      open={treeContextMenuState.mouseY !== null}
      onClose={handleTreeContextMenuClose}
      anchorReference="anchorPosition"
      anchorPosition={
        treeContextMenuState.mouseY !== null &&
          treeContextMenuState.mouseX !== null
          ? {
            top: treeContextMenuState.mouseY,
            left: treeContextMenuState.mouseX,
          }
          : undefined
      }
    >
      <MenuItem
        onClick={handleAddTreeNode}
        // [HP稼働] 階層制限を解除
        // disabled={!CanAddTreeNode(editNodeInfo.selectedNodeId)}
      >
        {textAddTreeNodeMenu}
      </MenuItem>
      <MenuItem onClick={handleRenameTreeNode}>
        {textRenameTreeNodeMenu}
      </MenuItem>
      <MenuItem onClick={handleRemoveTreeNode}>
        {TextRemoveTreeNodeMenu(editNodeInfo.selectedNodeId)}
      </MenuItem>
      <MenuItem
        onClick={handleTreeContextMenuOnMoveClick}
        disabled={
          !ExistsMoveTarget(
            getTreeDataById(configId)?.structure,
            editNodeInfo.selectedNodeId
          )
        }
      >
        {textMoveTreeNodeMenu}
      </MenuItem>
    </Menu>
  );

  /** コンテキストメニュー(フォルダ―移動) */
  const contextMenuOnMoveFolder = (
    <Menu
      keepMounted={false}
      open={treeContextMenuOnMove.mouseY !== null}
      onClose={handleTreeContextMenuClose}
      anchorReference="anchorPosition"
      anchorPosition={
        treeContextMenuOnMove.mouseY !== null &&
          treeContextMenuOnMove.mouseX !== null
          ? {
            top: treeContextMenuOnMove.mouseY,
            left: treeContextMenuOnMove.mouseX,
          }
          : undefined
      }
    >
      {/* 親は１件だが変数の使い方が不明なのでリストにして回す */}
      {PathToGrandParentNodeOnList(
        getTreeDataById(configId)?.structure,
        editNodeInfo.selectedNodeId
      ).map((grandParent: StructureType) => (
        <MenuItem
          key={grandParent.path}
          onClick={e => handleMoveTreeNode(e, grandParent.path)}
        >
          {`"${grandParent.name}"(１階層上)へ`}
        </MenuItem>
      ))}
      <Divider variant="middle" />
      {PathToBrothersNode(
        getTreeDataById(configId)?.structure,
        editNodeInfo.selectedNodeId
      ).map((brother: StructureType) => (
        <MenuItem
          key={brother.path}
          onClick={e => handleMoveTreeNode(e, brother.path)}
        >
          {`"${brother.name}"へ`}
        </MenuItem>
      ))}
    </Menu>
  );

  /** その他フォルダ― */
  const OtherTreeNodeRender = (parent: StructureType) => {
    const name = OtherTreeNodeName;
    const path = `${parent.path}/${name}`;
    const count = CountOtherTreeNode(parent);
    return (
      <VirtualFolderTreeItem
        key={path}
        configId={configId}
        parent={parent}
        nodeId={path}
        label={OtherTreeNodeLabel}
        count={count}
        isDisplayAll={isDisplayAll}
        isDisplaySelf={parent.isDisplaySelf}
        isDisplayInputText={false}
        isDummyNode
        editNodeInfo={editNodeInfo}
        getTreeDataById={getTreeDataById}
        setTreeDataById={setTreeDataById}
        handleClickNode={handleClickNode}
        setRenderNodeStates={setRenderNodeStates}
        handleTreeContextMenuClick={handleOtherTreeContextMenuClick}
      />
    );
  };

  /** 再帰的なツリーの描画 */
  const TreeRender = (node: StructureType) => {
    const hasChildren = node.children && node.children.length > 0;
    let isDisplaySelf = true;
    if (isDisplayAll === true) {
      // 全て表示する場合は必ず表示
      isDisplaySelf = true;
    } else {
      // それ以外はチェックボックスとフィルターのフラグを参照
      isDisplaySelf = node.isDisplaySelf && node.isDisplaySelfOnFilter;
    }
    // eslint-disable-next-line react/prop-types
    const isDisplayText = editNodeInfo.editNodeId === node.path;
    // 最上位ノードフラグ（カテゴリ名を含まない）
    const isTopLevelNode = node.path.split("/").length === 1;

    // 自分を表示する設定の場合
    if (isDisplaySelf) {
      if (hasChildren) {
        // 子ノードがある場合
        return (
          <VirtualFolderTreeItem
            key={node.path}
            configId={configId}
            parent={undefined}
            nodeId={node.path}
            label={node.name}
            count={node.count}
            isDisplayAll={isDisplayAll}
            isDisplaySelf={node.isDisplaySelf}
            isDisplayInputText={isDisplayText}
            isDummyNode={false}
            editNodeInfo={editNodeInfo}
            getTreeDataById={getTreeDataById}
            setTreeDataById={setTreeDataById}
            handleClickNode={handleClickNode}
            setRenderNodeStates={setRenderNodeStates}
            handleTreeContextMenuClick={handleTreeContextMenuClick}
          >
            {node.children.map(child => TreeRender(child))}
            {!isFiltered && !isTopLevelNode && OtherTreeNodeRender(node)}
          </VirtualFolderTreeItem>
        );
      }
      // 子ノードがない場合、上記と共通化すると展開可能になるので記述を分ける
      return (
        <VirtualFolderTreeItem
          key={node.path}
          configId={configId}
          parent={undefined}
          nodeId={node.path}
          label={node.name}
          count={node.count}
          isDisplayAll={isDisplayAll}
          isDisplaySelf={node.isDisplaySelf}
          isDisplayInputText={isDisplayText}
          isDummyNode={false}
          editNodeInfo={editNodeInfo}
          getTreeDataById={getTreeDataById}
          setTreeDataById={setTreeDataById}
          handleClickNode={handleClickNode}
          setRenderNodeStates={setRenderNodeStates}
          handleTreeContextMenuClick={handleTreeContextMenuClick}
        />
      );
    }
    // 非表示の設定の場合
    return <></>;
  };

  // 仮想フォルダー＋コンテキストメニュー

  //--------------------
  // コンポーネント
  return (
    <>
      <TreeView
        key={configId}
        className={classes.root}
        defaultCollapseIcon={<MinusSquareSvgIcon />}
        defaultExpandIcon={<PlusSquareSvgIcon />}
        defaultEndIcon={<CloseSquareSvgIcon color="disabled" />}
        expanded={expandedNode}
        onNodeToggle={(event, nodeIds) => handleNodeToggle(event, nodeIds)}
      >
        {ToNest(structure).map(root => TreeRender(root))}
      </TreeView>
      {
        isAdmin ? contextMenu : <></>
      }
      {contextMenuOnMoveFolder}
    </>
  );
};

React.memo(VirtualFolderTree);
